// Ablaufsteuerung des Messe-Kiosks: Phasen, Overlays und Verbindung von Spiellogik und 3D-Welt.
import * as THREE from "three"
import QRCode from "qrcode"
import gsap from "gsap"
import { DATA } from "./game/data.js"
import { baseline } from "./game/baseline.js"
import {
  newGame, setGuess, applicableActions, buyAction, canBuy, endRound, emissions, efficacy, eventFor,
  stationQuality, uncertainty, isTargeted, eventRemedies
} from "./game/engine.js"
import { result } from "./game/score.js"
import { runPayload } from "./game/payload.js"
import { encodeRun } from "./game/compact.js"
import { Stage } from "./stage.js"
import { Smoke } from "./fx/smoke.js"
import { World } from "./world/world.js"
import { CameraRig, VIEWS } from "./camera/rig.js"
import { playAnimation } from "./actions/animations.js"
import { Outbox, newToken } from "./outbox.js"
import { setBubbleGate, updateBubbles } from "./world/bubble.js"
import { html, escape, fragment, animateIn, countUp } from "./ui/dom.js"
import { formatTonnes, formatNumber, formatPercent } from "./ui/format.js"

const INDUSTRY_ICONS = { gear: "⚙️", flask: "🧪", wheat: "🌾", crane: "🏗️", truck: "🚚", laptop: "💻" }
const EMPLOYEE_STEPS = [10, 25, 50, 100, 250, 500, 1000, 2500]
const IDLE_WARNING_MS = 75000
const BUBBLE_MAX_DISTANCE = 110
const RESULT_TIMEOUT_MS = 120000

export class KioskApp {
  constructor(root) {
    this.root = root
    this.ui = root.querySelector("#ui")
    this.params = new URLSearchParams(window.location.search)
    this.quality = this.params.get("quality") === "low" ? "low" : "high"
    this.stage = new Stage({ canvas: root.querySelector("#stage"), labels: root.querySelector("#labels"), quality: this.quality })
    this.smoke = new Smoke(this.stage.scene, this.quality)
    this.world = new World(this.stage.scene, this.smoke, DATA)
    this.rig = new CameraRig(this.stage.camera, root)
    this.outbox = new Outbox(root.dataset.runsUrl)
    this.resultBaseUrl = root.dataset.resultBaseUrl
    this.leaderboardUrl = root.dataset.leaderboardUrl
    // Messe-Rechner (freigeschaltet) oder öffentlicher Online-Durchlauf
    this.booth = root.dataset.booth === "true"
    this.attractIndex = 0
    this.lastInput = Date.now()

    // Automatische Sprechblasen nur, wenn die Kamera nah am Werk ist und aufs Werk schaut
    setBubbleGate(() => this.stage.camera.position.length() < BUBBLE_MAX_DISTANCE && Math.hypot(this.rig.state.tx, this.rig.state.tz) < 20)
    this.stage.onUpdate((dt, time) => {
      updateBubbles()
      this.positionEggCard()
      this.smoke.update(dt)
      this.world.update(dt, time)
      this.rig.update(dt)
    })
    this.bindInput()
    if (this.params.has("debug")) this.enableStats()
  }

  start() {
    this.stage.start()
    if (this.params.has("poster")) return this.poster()
    this.attract()
    setInterval(() => this.checkIdle(), 1000)
  }

  // Standbild für das Social-Media-Vorschaubild (1200×630): Szene ohne Bedienelemente,
  // mit Text-Overlay. Erzeugt mit script/og_image.sh.
  poster() {
    this.phase = "poster"
    this.loadIndustry("maschinenbau", 250)
    this.world.showAllStationsInstantly()
    this.world.showLabels(this.world.stationIds(), false)
    for (const name of ["wind", "solar", "train", "flags", "circular"]) playAnimation(this.world, name)
    // Der Ballon würde hinter der Überschrift durchschimmern
    this.world.eggs.eggs.filter((egg) => egg.userData.egg.id === "balloon").forEach((egg) => (egg.visible = false))
    this.rig.state = { tx: 6, ty: 0, tz: 4, distance: 150, polar: 0.92, azimuth: 0.55 }
    this.rig.apply()
    this.stage.setShift(150, 0.01)
    this.mount(html`
      <div class="absolute inset-0 bg-gradient-to-r from-navy via-navy/85 to-transparent" style="width: 62%"></div>`, false)
    this.mount(html`
      <div class="absolute inset-y-0 left-0 flex w-[640px] flex-col justify-center px-14">
        <img src="${this.root.dataset.logoUrl}" alt="be2morrow" class="h-10 w-auto self-start">
        <p class="mt-8 text-sm font-semibold tracking-[0.22em] text-lime uppercase">Interaktives Planspiel · Klimaschutztag 2026</p>
        <h1 class="mt-3 text-[52px] leading-[1.05] font-bold">Wie viel CO₂ steckt wirklich in Ihrem Unternehmen?</h1>
        <p class="mt-5 text-xl text-white/80">Werk wählen, Lieferkette entdecken, CO₂ senken.</p>
        <p class="mt-8 inline-flex self-start rounded-full bg-lime px-6 py-3 text-lg font-bold text-navy">Jetzt spielen: klimaschutztag.be2morrow.de</p>
      </div>`, false)
    // Signal für den Screenshot: Szene ist aufgebaut, Animationen sind durchgelaufen
    setTimeout(() => (document.body.dataset.posterReady = "1"), 4500)
  }

  // --- Eingabe -----------------------------------------------------------

  bindInput() {
    const markActive = () => (this.lastInput = Date.now())
    for (const type of ["pointerdown", "pointermove", "wheel", "keydown"]) window.addEventListener(type, markActive, { passive: true })

    this.ui.addEventListener("click", (event) => {
      const target = event.target.closest("[data-action]")
      if (!target || target.disabled) return
      const handler = this[`on_${target.dataset.action}`]
      if (handler) handler.call(this, target, event)
    })
    this.ui.addEventListener("input", (event) => {
      const handler = this[`input_${event.target.dataset.input}`]
      if (handler) handler.call(this, event.target)
    })
    this.root.querySelector("#labels").addEventListener("click", (event) => {
      const label = event.target.closest("[data-station]")
      if (label && this.exploreEnabled) this.openStation(label.dataset.station)
    })
    this.root.addEventListener("click", (event) => {
      if (this.phase === "attract" && !event.target.closest("[data-ui]")) this.chooseIndustry()
    })
    window.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && event.shiftKey) this.attract()
    })
    this.bindEggs()
  }

  // Klickbares in der Welt: Figuren auf dem Werkshof und Fundstücke in der Landschaft.
  bindEggs() {
    const raycaster = new THREE.Raycaster()
    const pointer = new THREE.Vector2()
    const canvas = this.root.querySelector("#stage")
    const targetAt = (event) => {
      const people = this.peopleEnabled()
      const eggs = this.eggsEnabled()
      if (!people && !eggs) return null
      pointer.set((event.clientX / window.innerWidth) * 2 - 1, -(event.clientY / window.innerHeight) * 2 + 1)
      raycaster.setFromCamera(pointer, this.stage.camera)
      const figure = people ? this.world.pickPerson(raycaster) : null
      if (figure) return { figure }
      const gag = people ? this.world.pickGag(raycaster) : null
      if (gag) return { gag }
      const egg = eggs ? this.world.pickEgg(raycaster) : null
      return egg ? { egg } : null
    }
    let lastHover = 0
    canvas.addEventListener("pointermove", (event) => {
      const now = performance.now()
      if (now - lastHover < 80) return
      lastHover = now
      canvas.style.cursor = targetAt(event) ? "pointer" : ""
    })
    canvas.addEventListener("click", (event) => {
      if (this.rig.wasDrag()) return
      const target = targetAt(event)
      if (target?.figure) return this.world.pokePerson(target.figure)
      if (target?.gag) return this.world.clickGag(target.gag)
      if (!target?.egg) return
      const egg = target.egg
      this.foundEggs.add(egg.id)
      const total = this.world.allEggs().length
      this.showEggCard(egg.object, { title: egg.title, text: egg.text, footer: `Fundstück ${this.foundEggs.size} von ${total}` })
    })
  }

  // Infokarte zu einem Fundstück: sitzt über dem Objekt (bei Platzmangel darunter),
  // bleibt im Bild und lässt sich per ✕ schließen.
  showEggCard(object, { title, text, footer }) {
    this.closeEggCard()
    object.updateMatrixWorld(true)
    const box = new THREE.Box3().setFromObject(object)
    const anchor = object.worldToLocal(new THREE.Vector3((box.min.x + box.max.x) / 2, box.max.y + 0.5, (box.min.z + box.max.z) / 2))
    const element = this.mount(html`
      <div data-egg-card class="pointer-events-auto absolute top-0 left-0 z-30 w-[320px] rounded-2xl bg-white p-4 pr-11 text-navy shadow-2xl">
        <button type="button" data-action="close_egg_card" class="absolute top-2 right-2 flex size-9 items-center justify-center rounded-full text-lg text-muted hover:bg-navy/5 hover:text-navy" aria-label="Schließen">✕</button>
        <p class="text-[16px] font-bold">🥚 ${title}</p>
        <p class="mt-1 text-[15px] leading-snug">${text}</p>
        <p class="mt-2 text-xs text-muted">${footer}</p>
        <span data-arrow class="absolute size-4 rotate-45 bg-white"></span>
      </div>`, false)
    this.eggCard = { element, object, anchor }
    this.positionEggCard()
    this.eggCardTimer = setTimeout(() => this.closeEggCard(), 15000)
  }

  on_close_egg_card() {
    this.closeEggCard()
  }

  closeEggCard() {
    clearTimeout(this.eggCardTimer)
    this.eggCard?.element.remove()
    this.eggCard = null
  }

  positionEggCard() {
    if (!this.eggCard) return
    const { element, object, anchor } = this.eggCard
    if (!object.parent) return this.closeEggCard()
    const point = object.localToWorld(anchor.clone()).project(this.stage.camera)
    const hidden = point.z > 1
    element.style.visibility = hidden ? "hidden" : "visible"
    if (hidden) return
    const x = (point.x + 1) / 2 * window.innerWidth
    const y = (1 - point.y) / 2 * window.innerHeight
    const width = element.offsetWidth
    const height = element.offsetHeight
    const margin = 16
    const above = y - height - 18 > 110
    const left = THREE.MathUtils.clamp(x - width / 2, margin, window.innerWidth - width - margin)
    const top = THREE.MathUtils.clamp(above ? y - height - 18 : y + 18, margin, window.innerHeight - height - margin)
    element.style.transform = `translate(${Math.round(left)}px, ${Math.round(top)}px)`
    const arrow = element.querySelector("[data-arrow]")
    arrow.style.left = `${THREE.MathUtils.clamp(x - left - 8, 16, width - 32)}px`
    arrow.style.top = above ? "auto" : "-8px"
    arrow.style.bottom = above ? "-8px" : "auto"
  }

  peopleEnabled() {
    return ["industry", "factory", "guess", "explore", "rounds"].includes(this.phase) && !this.busy
  }

  eggsEnabled() {
    return ["factory", "guess", "explore", "rounds"].includes(this.phase) && !this.busy
  }

  checkIdle() {
    // Inaktivitäts-Reset nur am Messe-Rechner, nicht beim Online-Spiel
    if (!this.booth) return
    const idle = Date.now() - this.lastInput
    if (this.phase === "attract") return
    if (this.phase === "result" && idle > RESULT_TIMEOUT_MS) return this.attract()
    if (this.phase !== "result" && idle > IDLE_WARNING_MS && !this.idleModal) this.showIdleWarning()
  }

  showIdleWarning() {
    let seconds = 10
    this.idleModal = this.modal(html`
      <p class="text-2xl font-bold">Noch da?</p>
      <p class="mt-2 text-white/70">Das Spiel startet in <span data-countdown>${seconds}</span> Sekunden neu.</p>
      <button data-action="stay" class="btn btn-lime mt-6">Weiterspielen</button>`)
    const startedAt = this.lastInput
    const timer = setInterval(() => {
      seconds -= 1
      const counter = this.idleModal?.querySelector("[data-countdown]")
      if (counter) counter.textContent = seconds
      if (this.lastInput !== startedAt && seconds < 9) return this.on_stay()
      if (seconds <= 0) {
        clearInterval(timer)
        this.idleModal = null
        this.attract()
      }
    }, 1000)
    this.idleTimer = timer
  }

  on_stay() {
    clearInterval(this.idleTimer)
    this.idleModal?.remove()
    this.idleModal = null
    this.lastInput = Date.now()
  }

  // --- Overlays ------------------------------------------------------------

  clearUi() {
    this.closeEggCard()
    this.ui.innerHTML = ""
    this.idleModal = null
  }

  mount(markup, options) {
    const element = fragment(markup)
    element.dataset.ui = ""
    this.ui.appendChild(element)
    if (options !== false) animateIn(element, options)
    return element
  }

  modal(inner) {
    return this.mount(html`
      <div class="pointer-events-auto absolute inset-0 z-40 flex items-center justify-center bg-navy/60 backdrop-blur-sm">
        <div class="max-w-xl rounded-3xl bg-navy p-10 text-center shadow-2xl ring-1 ring-white/15">${inner}</div>
      </div>`)
  }

  brand() {
    return html`
      <button type="button" data-action="logo" title="Zur Übersicht / Neu starten"
              class="pointer-events-auto absolute top-6 left-8 rounded-2xl bg-navy/80 px-5 py-3 text-left shadow-xl ring-1 ring-white/10 backdrop-blur transition hover:bg-navy hover:ring-white/25">
        <img src="${this.root.dataset.logoUrl}" alt="be2morrow" class="h-8 w-auto">
        <p class="mt-2 text-xs font-semibold tracking-[0.18em] text-white/60 uppercase">Klimaschutztag 2026</p>
      </button>`
  }

  // Zähler oben in der Mitte: Gesamtemissionen und Aufteilung Scope 1+2 / 3.
  renderCounter({ revealed }) {
    this.ui.querySelector("[data-counter]")?.remove()
    const current = this.currentEmissions()
    this.counter = this.mount(html`
      <div data-counter class="absolute top-6 left-1/2 w-[440px] -translate-x-1/2 rounded-2xl bg-navy/85 px-6 py-4 shadow-xl ring-1 ring-white/10 backdrop-blur">
        <div class="flex items-baseline justify-between">
          <span class="text-xs font-semibold tracking-[0.18em] text-white/60 uppercase">Ihr CO₂-Fußabdruck / Jahr</span>
          <span data-delta class="text-sm font-semibold text-lime"></span>
        </div>
        <p data-total class="mt-1 text-3xl font-bold">${formatTonnes(revealed ? current.total : current.scope12)}</p>
        <div class="mt-3 flex h-2.5 overflow-hidden rounded-full bg-white/10">
          <div data-bar12 class="bg-green transition-all duration-1000" style="width:${revealed ? (current.scope12 / current.total) * 100 : 100}%"></div>
          <div data-bar3 class="bg-slate transition-all duration-1000" style="width:${revealed ? (current.scope3 / current.total) * 100 : 0}%"></div>
        </div>
        <div class="mt-1.5 flex justify-between text-xs text-white/70">
          <span>Scope 1 + 2: <strong data-s12 class="text-white">${formatTonnes(current.scope12)}</strong></span>
          <span>Scope 3: <strong data-s3 class="text-white">${revealed ? formatTonnes(current.scope3) : "?"}</strong></span>
        </div>
      </div>`, false)
  }

  updateCounter({ animateFrom } = {}) {
    if (!this.counter) return
    const current = this.currentEmissions()
    const total = this.counter.querySelector("[data-total]")
    countUp(total, animateFrom ?? this.lastTotal ?? current.total, current.total, { duration: 1400, format: formatTonnes })
    this.counter.querySelector("[data-s12]").textContent = formatTonnes(current.scope12)
    this.counter.querySelector("[data-s3]").textContent = formatTonnes(current.scope3)
    this.counter.querySelector("[data-bar12]").style.width = `${(current.scope12 / current.total) * 100}%`
    this.counter.querySelector("[data-bar3]").style.width = `${(current.scope3 / current.total) * 100}%`
    const base = this.base.total
    const delta = 1 - current.total / base
    this.counter.querySelector("[data-delta]").textContent = delta > 0.0005 ? `−${formatPercent(delta, 1)} seit 2025` : ""
    this.lastTotal = current.total
  }

  currentEmissions() {
    return this.game ? emissions(DATA, this.game) : this.base
  }

  // --- Phase 0: Attract ----------------------------------------------------

  attract() {
    this.phase = "attract"
    this.game = null
    this.exploreEnabled = false
    this.rig.interactive = false
    clearInterval(this.idleTimer)
    this.clearUi()
    const industries = Object.keys(DATA.industries.industries)
    const industry = industries[this.attractIndex++ % industries.length]
    this.world.setConnectionsVisible(true)
    this.loadIndustry(industry, 120)
    this.world.showAllStationsInstantly()
    this.world.showLabels(this.world.stationIds(), false)
    this.rig.fly(VIEWS.attract, { duration: 2.5 })
    this.rig.autoRotate = 0.06
    this.stage.setShift(0)

    this.mount(html`
      <div class="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-navy via-navy/70 to-transparent px-16 pt-40 pb-14">
        <p class="text-sm font-semibold tracking-[0.25em] text-lime uppercase">be2morrow · Klimaschutztag 2026</p>
        <h1 class="mt-3 max-w-4xl text-6xl leading-[1.05] font-bold">Wie viel CO₂ steckt wirklich in Ihrem Unternehmen?</h1>
        <p class="mt-5 max-w-2xl text-xl text-white/75">Bauen Sie Ihr Werk, zoomen Sie in die Lieferkette und senken Sie die Emissionen bis 2030. Vier Minuten – mit Bestenliste und Tagesgewinn.</p>
        <div class="mt-8 inline-flex animate-pulse items-center gap-3 rounded-full bg-lime px-8 py-4 text-xl font-bold text-navy">Klicken zum Starten</div>
      </div>`, { from: "translate-y-8" })
    this.mount(this.brand(), false)
    this.mount(html`
      <div class="absolute right-8 bottom-12 w-[560px] rounded-2xl bg-white px-5 pt-3 pb-4 shadow-xl">
        <p class="text-[11px] font-semibold tracking-[0.18em] text-muted uppercase">Partner & Förderer</p>
        <img src="${this.root.dataset.trustUrl}" alt="Universität Kassel, Wege zum klimaneutralen Unternehmen, upp, digitales.hessen DISTR@L" class="mt-2 w-full">
      </div>`, { from: "translate-y-4", delay: 400 })
    this.leaderboardBox = this.mount(html`
      <div class="absolute top-6 right-8 w-80 rounded-2xl bg-navy/80 p-5 ring-1 ring-white/10 backdrop-blur">
        <p class="text-xs font-semibold tracking-[0.18em] text-white/60 uppercase">Bestenliste heute</p>
        <ol data-entries class="mt-3 space-y-2 text-sm"><li class="text-white/50">Noch frei – holen Sie sich Platz 1!</li></ol>
      </div>`, { from: "translate-x-4" })
    this.refreshLeaderboard()
  }

  async refreshLeaderboard() {
    try {
      const response = await fetch(this.leaderboardUrl, { headers: { Accept: "application/json" } })
      const entries = await response.json()
      const list = this.leaderboardBox?.querySelector("[data-entries]")
      if (!list || entries.length === 0) return
      list.innerHTML = entries.slice(0, 6).map((entry, i) => html`
        <li class="flex items-center gap-3">
          <span class="w-5 text-right font-bold ${i === 0 ? "text-lime" : "text-white/50"}">${i + 1}</span>
          <span class="min-w-0 flex-1 truncate">${escape(entry.name)}<span class="block text-xs text-white/50">${escape(entry.industry)}</span></span>
          <span class="text-lg font-bold">${entry.score}</span>
        </li>`).join("")
      this.todayBest = entries[0]?.score
    } catch {
      // offline – Bestenliste bleibt leer
    }
  }

  loadIndustry(industry, employees) {
    this.base = baseline(DATA, industry, employees)
    this.world.setIndustry(industry, employees, this.base.stations)
    for (const [id, tonnes] of Object.entries(this.base.stations)) this.world.setStationTonnes(id, tonnes, { animate: false })
    if (this.phase !== "attract") this.world.stations.factory.cloud.setSize(0, { duration: 0.01 })
    const quality = stationQuality(DATA, { owned: {}, eventResults: [], baseSources: {} })
    for (const id of this.world.stationIds()) this.world.setQuality(id, quality[id], uncertainty(quality[id]))
    this.world.showQuality(false)
  }

  // --- Phase 1: Branche und Größe -------------------------------------------

  chooseIndustry() {
    this.phase = "industry"
    this.rig.autoRotate = 0
    this.lastInput = Date.now()
    this.token = newToken()
    this.foundEggs = new Set()
    this.seed = Math.floor(Math.random() * 2 ** 31)
    this.selection = { industry: null, employees: 100 }
    this.clearUi()
    this.mount(this.brand(), false)
    this.world.setConnectionsVisible(false)
    this.loadIndustry("maschinenbau", 100)
    this.world.showLabels(this.world.stationIds(), false)
    this.rig.fly(VIEWS.factory, { duration: 2.4 })
    this.stage.setShift(280)

    const tiles = Object.entries(DATA.industries.industries).map(([key, industry]) => html`
      <button data-action="pick_industry" data-industry="${key}"
              class="industry-tile flex items-center gap-4 rounded-2xl bg-white/5 p-4 text-left ring-1 ring-white/10 transition hover:bg-white/10">
        <span class="text-4xl">${INDUSTRY_ICONS[industry.icon]}</span>
        <span><span class="block text-lg font-semibold">${industry.label}</span><span class="block text-sm text-white/60">${industry.tagline}</span></span>
      </button>`).join("")

    this.mount(html`
      <div class="pointer-events-auto absolute top-28 bottom-10 left-8 flex w-[520px] flex-col rounded-3xl bg-navy/90 p-8 shadow-2xl ring-1 ring-white/10 backdrop-blur">
        <p class="text-xs font-semibold tracking-[0.18em] text-lime uppercase">Schritt 1 von 4</p>
        <h2 class="mt-2 text-3xl font-bold">Welches Werk ist Ihres?</h2>
        <p class="mt-2 text-white/70">Wir rechnen mit einem typischen Unternehmen Ihrer Branche.</p>
        <div class="mt-6 grid grid-cols-2 gap-3">${tiles}</div>
        <div class="mt-8">
          <div class="flex items-baseline justify-between">
            <label class="text-lg font-semibold" for="employees">Mitarbeitende</label>
            <span data-employees class="text-3xl font-bold text-lime">100</span>
          </div>
          <input id="employees" type="range" min="0" max="${EMPLOYEE_STEPS.length - 1}" step="1" value="3" data-input="employees" class="kiosk-range mt-3 w-full">
          <div class="mt-1 flex justify-between text-xs text-white/50"><span>10</span><span>2.500</span></div>
        </div>
        <button data-action="confirm_industry" disabled class="btn btn-lime mt-auto w-full py-4 text-lg disabled:opacity-40">Das ist mein Werk →</button>
      </div>`, { from: "-translate-x-6" })
  }

  on_pick_industry(button) {
    this.selection.industry = button.dataset.industry
    this.ui.querySelectorAll(".industry-tile").forEach((tile) => {
      const active = tile === button
      tile.classList.toggle("ring-lime", active)
      tile.classList.toggle("ring-2", active)
      tile.classList.toggle("bg-white/15", active)
    })
    this.ui.querySelector("[data-action=confirm_industry]").disabled = false
    this.previewFactory()
  }

  input_employees(input) {
    this.selection.employees = EMPLOYEE_STEPS[Number(input.value)]
    this.ui.querySelector("[data-employees]").textContent = formatNumber(this.selection.employees)
    this.world.setEmployees(this.selection.employees)
  }

  previewFactory() {
    const { industry, employees } = this.selection
    this.loadIndustry(industry || "maschinenbau", employees)
    const factory = this.world.factory.group
    factory.scale.setScalar(0.001)
    gsap.to(factory.scale, { x: 1, y: 1, z: 1, duration: 0.7, ease: "back.out(1.6)" })
  }

  // --- Phase 2: Werk, Scope 1 + 2 -------------------------------------------

  on_confirm_industry() {
    const { industry, employees } = this.selection
    this.phase = "factory"
    this.game = newGame(DATA, { industry, employees, seed: this.seed })
    this.loadIndustry(industry, employees)
    this.clearUi()
    this.mount(this.brand(), false)
    this.renderCounter({ revealed: false })
    this.rig.fly(VIEWS.factoryClose, { duration: 1.8 })
    this.world.setSmoke("factory", true)
    this.world.showLabels(["factory"])
    this.world.stations.factory.cloud.setSize(this.world.cloudScale(this.base.stations.factory), { delay: 0.8 })

    // Branchenspezifische Bezeichnungen; der größte direkte Posten wird hervorgehoben
    const scope12 = DATA.industries.industries[industry].scope12
    const keys = ["heat", "fleet", "power"]
    const biggest = keys.reduce((best, key) => (this.base.sources[key] > this.base.sources[best] ? key : best))
    const rows = keys.map((key) => {
      const { icon, label, note } = scope12.sources[key]
      const top = key === biggest
      return html`
        <li class="flex items-center gap-4 rounded-2xl px-4 py-3 ${top ? "bg-lime/10 ring-2 ring-lime" : "bg-white/5"}">
          <span class="text-3xl">${icon}</span>
          <span class="flex-1">
            <span class="block font-semibold">${label}</span>
            <span class="block text-sm text-white/60">${note}${top ? ' · <span class="font-semibold text-lime">größter Posten</span>' : ""}</span>
          </span>
          <span class="text-lg font-bold">${formatTonnes(this.base.sources[key])}</span>
        </li>`
    }).join("")

    this.mount(html`
      <div class="pointer-events-auto absolute bottom-10 left-8 w-[520px] rounded-3xl bg-navy/90 p-8 shadow-2xl ring-1 ring-white/10 backdrop-blur">
        <p class="text-xs font-semibold tracking-[0.18em] text-lime uppercase">Schritt 2 von 4 · Ihr Werk</p>
        <h2 class="mt-2 text-3xl font-bold">Das sind Ihre direkten Emissionen</h2>
        <ul class="mt-5 space-y-2">${rows}</ul>
        <p class="mt-4 font-medium text-white/90">${scope12.insights[biggest]}</p>
        <p class="mt-2 text-white/70">${scope12.closing}</p>
        <button data-action="to_guess" class="btn btn-lime mt-6 w-full py-4 text-lg">Und was ist mit dem Rest? →</button>
      </div>`, { from: "-translate-x-6", delay: 600 })
  }

  // --- Phase 3: Schätzen ----------------------------------------------------

  on_to_guess(button) {
    button.closest("[data-ui]").remove()
    this.phase = "guess"
    this.stage.setShift(0)
    this.guess = 50
    this.mount(html`
      <div class="pointer-events-auto absolute bottom-10 left-1/2 w-[760px] -translate-x-1/2 rounded-3xl bg-navy/90 p-8 shadow-2xl ring-1 ring-white/10 backdrop-blur">
        <p class="text-xs font-semibold tracking-[0.18em] text-lime uppercase">Schritt 3 von 4 · Ihre Schätzung</p>
        <h2 class="mt-2 text-3xl font-bold">Welcher Anteil Ihrer Emissionen entsteht <span class="text-lime">außerhalb</span> Ihres Werkstors?</h2>
        <p class="mt-2 text-white/70">Bei Lieferanten, im Transport, auf dem Arbeitsweg, bei der Nutzung und Entsorgung Ihrer Produkte – das ist Scope 3.</p>
        <div class="mt-8 flex items-center gap-6">
          <span class="text-sm text-white/60">im Werk</span>
          <input type="range" min="0" max="100" value="50" data-input="guess" class="kiosk-range flex-1">
          <span class="text-sm text-white/60">außerhalb</span>
          <span data-guess class="w-28 text-right text-5xl font-bold text-lime">50 %</span>
        </div>
        <div class="mt-6 flex items-center justify-between">
          <p class="text-sm text-white/60">Bis zu 10 Punkte für eine gute Schätzung.</p>
          <button data-action="reveal" class="btn btn-lime px-10 py-4 text-lg">Auflösen →</button>
        </div>
      </div>`)
  }

  input_guess(input) {
    this.guess = Number(input.value)
    this.ui.querySelector("[data-guess]").textContent = `${this.guess} %`
  }

  // --- Phase 4: Rauszoom ----------------------------------------------------

  async on_reveal(button) {
    button.closest("[data-ui]").remove()
    this.phase = "reveal"
    this.game = setGuess(this.game, this.guess)
    this.world.showLabels(["factory"], false)
    const flight = this.rig.fly(VIEWS.world, { duration: 4.2, ease: "power3.inOut" })
    const revealDuration = this.world.revealStations({ delay: 1.2, stagger: 0.22 })
    const counterTotal = this.counter.querySelector("[data-total]")
    setTimeout(() => {
      countUp(counterTotal, this.base.scope12, this.base.total, { duration: revealDuration * 1000 - 1000, format: formatTonnes })
      this.counter.querySelector("[data-s3]").textContent = formatTonnes(this.base.scope3)
      this.counter.querySelector("[data-bar12]").style.width = `${(this.base.scope12 / this.base.total) * 100}%`
      this.counter.querySelector("[data-bar3]").style.width = `${(this.base.scope3 / this.base.total) * 100}%`
    }, 1200)
    await flight
    await new Promise((resolve) => setTimeout(resolve, Math.max(0, revealDuration * 1000 - 4200)))
    this.world.showLabels(this.world.stationIds())
    this.lastTotal = this.base.total
    this.showRevealResult()
  }

  showRevealResult() {
    this.phase = "explore"
    this.exploreEnabled = true
    this.stage.setShift(260)
    this.rig.interactive = true
    this.rig.limits = { minDistance: 40, maxDistance: 240 }
    const share = this.base.scope3Share
    const points = Math.round(10 * Math.max(0, 1 - Math.abs(this.guess - share * 100) / 40))
    const top = Object.entries(this.base.stations).filter(([id]) => id !== "factory").sort((a, b) => b[1] - a[1])[0]
    this.mount(html`
      <div data-reveal-panel class="pointer-events-auto absolute bottom-10 left-8 w-[540px] rounded-3xl bg-navy/90 p-8 shadow-2xl ring-1 ring-white/10 backdrop-blur">
        <p class="text-xs font-semibold tracking-[0.18em] text-lime uppercase">Die Auflösung</p>
        <div class="mt-3 flex items-end gap-6">
          <div><p class="text-7xl font-bold text-lime">${formatPercent(share)}</p><p class="text-white/70">Ihrer Emissionen sind Scope 3</p></div>
          <div class="mb-2 rounded-2xl bg-white/5 px-4 py-3 text-center">
            <p class="text-sm text-white/60">Ihre Schätzung</p>
            <p class="text-2xl font-bold">${this.guess} %</p>
            <p class="text-sm font-semibold text-lime">+${points} Punkte</p>
          </div>
        </div>
        <p class="mt-5 text-white/80">Ihr Werk ist nur die Spitze des Eisbergs. Größter Block: <strong>${DATA.stations.stations[top[0]].label}</strong> mit ${formatTonnes(top[1])}.</p>
        <p class="mt-2 text-sm text-white/60">👆 Klicken Sie auf eine Station, um hineinzuzoomen. Mausrad zoomt, Ziehen dreht.</p>
        <button data-action="start_rounds" class="btn btn-lime mt-6 w-full py-4 text-lg">Jetzt handeln: Emissionen senken →</button>
      </div>`, { from: "-translate-x-6" })
  }

  openStation(id) {
    if (this.busy) return
    const config = DATA.stations.stations[id]
    const quality = stationQuality(DATA, this.game)[id]
    const tonnes = this.currentEmissions().stations[id]
    const known = isTargeted(this.game, 99) || id === "factory"
    this.ui.querySelector("[data-station-panel]")?.remove()
    // Panels links/rechts ausblenden, damit die Station frei liegt
    this.ui.querySelectorAll("[data-reveal-panel], [data-actions], [data-toast]").forEach((panel) => panel.classList.add("hidden"))
    this.world.focusStation(id)
    this.rig.flyToPoint(this.world.stationPosition(id), { distance: 40, polar: 1.02, duration: 1.4 })
    this.stage.setShift(-230)
    this.mount(html`
      <div data-station-panel class="pointer-events-auto absolute top-36 right-8 w-[420px] rounded-3xl bg-white p-7 text-navy shadow-2xl">
        <p class="text-xs font-semibold tracking-[0.18em] text-green uppercase">${config.ghg}</p>
        <h3 class="mt-1 text-2xl font-bold">${config.label}</h3>
        <p class="mt-1 text-3xl font-bold">${formatTonnes(tonnes)}<span class="text-base font-normal text-muted"> / Jahr</span></p>
        <p class="mt-4 text-muted">${config.text}</p>
        <div class="mt-4 rounded-2xl bg-paper p-4">
          <p class="text-sm font-semibold">So wird gerechnet</p>
          <p class="mt-1 text-sm text-muted">${config.method}</p>
          <p class="mt-3 text-sm"><span class="font-semibold">Datenqualität ${quality}</span> · Unsicherheit ±${Math.round(uncertainty(quality) * 100)} %
          ${known ? "" : '<span class="block text-xs text-muted">Ohne Klimabilanz bleibt das eine Schätzung über Durchschnittswerte.</span>'}</p>
        </div>
        <button data-action="close_station" class="btn mt-5 w-full bg-navy text-white hover:bg-navy-700">Zurück zur Übersicht</button>
      </div>`, { from: "translate-x-6" })
  }

  on_close_station() {
    this.backToOverview()
  }

  backToOverview() {
    this.ui.querySelector("[data-station-panel]")?.remove()
    this.ui.querySelectorAll("[data-reveal-panel], [data-actions]").forEach((panel) => panel.classList.remove("hidden"))
    this.world.focusStation(null)
    this.rig.fly(VIEWS.world, { duration: 1.4 })
    this.stage.setShift(this.phase === "rounds" ? -330 : 260)
  }

  // Liegt die Kamera ungefähr in der Übersichtsposition (nicht hineingezoomt)?
  atOverview() {
    const { tx, tz, distance } = this.rig.state
    const [wx, , wz] = VIEWS.world.target
    return Math.abs(distance - VIEWS.world.distance) < 25 && Math.hypot(tx - wx, tz - wz) < 10
  }

  // Logo oben links: aus der Detailansicht zurück zur Übersicht, aus der Übersicht neu starten.
  on_logo() {
    if (this.busy || this.phase === "attract" || this.phase === "poster") return
    const inGame = ["explore", "rounds"].includes(this.phase)
    if (inGame && (this.ui.querySelector("[data-station-panel]") || !this.atOverview())) return this.backToOverview()
    if (!inGame) return this.attract()
    if (this.ui.querySelector("[data-restart-confirm]")) return
    const dialog = this.modal(html`
      <p class="text-2xl font-bold">Spiel neu starten?</p>
      <p class="mt-2 text-white/70">Ihr bisheriger Fortschritt in diesem Spiel geht verloren.</p>
      <div class="mt-6 flex justify-center gap-3">
        <button data-action="cancel_restart" class="btn btn-ghost px-8">Weiterspielen</button>
        <button data-action="restart" class="btn btn-lime px-8">Neu starten</button>
      </div>`)
    dialog.dataset.restartConfirm = ""
  }

  on_cancel_restart(button) {
    button.closest("[data-ui]").remove()
  }

  // --- Phase 5: Aktionsrunden ------------------------------------------------

  on_start_rounds() {
    this.phase = "rounds"
    this.exploreEnabled = true
    this.clearUi()
    this.mount(this.brand(), false)
    this.renderCounter({ revealed: true })
    this.updateCounter({ animateFrom: this.base.total })
    this.rig.fly(VIEWS.world, { duration: 1.2 })
    this.stage.setShift(-330)
    this.startRound()
  }

  year(round = this.game.round) {
    return DATA.actions.rounds[round]
  }

  startRound() {
    const event = eventFor(DATA, this.game)
    const round = this.game.round
    this.modal(html`
      <p class="text-sm font-semibold tracking-[0.2em] text-lime uppercase">Runde ${round + 1} von ${DATA.actions.rounds.length}</p>
      <p class="mt-2 text-7xl font-bold">${this.year()}</p>
      <p class="mt-3 text-white/70">Ihr Klimabudget: <strong class="text-white">${this.game.budget} Punkte</strong>${round > 0 ? " (inkl. Übertrag)" : ""}</p>
      ${round === 0 ? html`
        <div class="mt-6 rounded-2xl bg-white/5 p-5 text-left">
          <p class="text-xs font-semibold tracking-[0.18em] text-lime uppercase">So funktioniert’s</p>
          <ol class="mt-2 space-y-2 text-[15px] text-white/85">
            <li><strong class="text-white">1. Maßnahmen wählen:</strong> Jede Maßnahme kostet Budgetpunkte (●) und senkt Ihre Emissionen dauerhaft. Nicht genutzte Punkte nehmen Sie ins nächste Jahr mit.</li>
            <li><strong class="text-white">2. Ereignis abwarten:</strong> Am Jahresende tritt das angekündigte Ereignis ein. Wer passend vorgesorgt hat, bekommt Bonuspunkte.</li>
            <li><strong class="text-white">3. Drei Runden bis 2030:</strong> Am Ende zählt, wie stark und wie effizient Sie Ihre Emissionen gesenkt haben.</li>
          </ol>
        </div>` : ""}
      <div class="mt-4 rounded-2xl bg-white/5 p-5 text-left ring-1 ring-warn/40">
        <p class="text-xs font-semibold tracking-[0.18em] text-warn uppercase">Ereignis am Jahresende</p>
        <p class="mt-1 text-xl font-semibold">${event.title}</p>
        <p class="mt-1 text-white/70">${event.text}</p>
        <p class="mt-3 text-sm font-semibold text-lime">🛡 Vorsorgen mit: ${eventRemedies(DATA, this.game, event).join(" oder ")}</p>
      </div>
      <button data-action="begin_round" class="btn btn-lime mt-8 px-10 py-4 text-lg">Los geht’s</button>`)
  }

  on_begin_round(button) {
    button.closest("[data-ui]").remove()
    this.renderStatus()
    this.renderActions()
  }

  renderStatus() {
    this.ui.querySelector("[data-status]")?.remove()
    this.mount(html`
      <div data-status class="absolute top-6 right-8 flex items-center gap-3">
        <div class="rounded-2xl bg-navy/85 px-5 py-3 text-center ring-1 ring-white/10 backdrop-blur">
          <p class="text-xs text-white/60">Jahr</p><p class="text-2xl font-bold">${this.year()}</p>
        </div>
        <div class="rounded-2xl bg-lime px-5 py-3 text-center text-navy">
          <p class="text-xs font-semibold">Klimabudget</p><p data-budget class="text-2xl font-bold">${this.game.budget}</p>
        </div>
      </div>`, false)
  }

  renderActions() {
    this.ui.querySelector("[data-actions]")?.remove()
    const ids = applicableActions(DATA, this.game)
    const current = emissions(DATA, this.game).total
    const event = eventFor(DATA, this.game)
    const cards = ids.map((id) => {
      const action = DATA.actions.actions[id]
      const owned = this.game.owned[id]
      const affordable = canBuy(DATA, this.game, id)
      const saving = owned === undefined ? current - emissions(DATA, { ...this.game, owned: { ...this.game.owned, [id]: this.game.round } }).total : 0
      const weak = action.scope === "3" && !action.enables_targeting && efficacy(DATA, this.game, id) < 1
      const scopeChip = action.scope === "3"
        ? '<span class="shrink-0 whitespace-nowrap rounded-full bg-slate/30 px-2 py-0.5 text-[11px] font-semibold">Scope 3</span>'
        : '<span class="shrink-0 whitespace-nowrap rounded-full bg-green/30 px-2 py-0.5 text-[11px] font-semibold">Scope 1 + 2</span>'
      const coins = `<span class="whitespace-nowrap text-sm tracking-wider text-lime" title="Kosten: ${action.cost} Punkte">${"●".repeat(action.cost)}</span>`
      const protects = owned === undefined && event.requires_any.includes(id)
      const recommended = owned === undefined && action.enables_targeting
      let footer
      if (owned !== undefined) footer = `<span class="font-semibold text-lime">✓ umgesetzt ${this.year(owned)}</span>`
      else if (action.enables_targeting) footer = '<span class="text-white/70">Zeigt Ihre echten Scope-3-Hotspots. Danach wirken Scope-3-Maßnahmen voll.</span>'
      else footer = `<span class="font-semibold">≈ −${formatNumber(saving)} t CO₂e pro Jahr</span>${weak ? '<span class="text-warn">½ Wirkung – erst Klimabilanz</span>' : ""}`
      const tags = [
        recommended ? '<span class="rounded-full bg-lime px-2 py-0.5 text-[11px] font-bold text-navy">Empfehlung: zuerst</span>' : "",
        protects ? '<span class="rounded-full bg-warn/20 px-2 py-0.5 text-[11px] font-bold text-warn">🛡 hilft beim Ereignis</span>' : ""
      ].join("")
      return html`
        <button data-action="buy" data-id="${id}" data-station="${action.station}" ${owned !== undefined || !affordable ? "disabled" : ""}
                class="action-card flex min-h-[132px] flex-col rounded-2xl p-3.5 text-left ring-1 transition
                       ${owned !== undefined ? "bg-green/20 ring-2 ring-green" : "bg-white/5 ring-white/10 hover:bg-white/10"}
                       disabled:cursor-default ${owned === undefined && !affordable ? "opacity-40" : ""}">
          <span class="flex items-center justify-between gap-2">${scopeChip}${coins}</span>
          <span class="mt-2 text-[16px] leading-snug font-semibold">${action.label}</span>
          ${tags ? `<span class="mt-1.5 flex flex-wrap gap-1">${tags}</span>` : ""}
          <span class="mt-auto flex flex-col gap-0.5 pt-2 text-xs leading-tight">${footer}</span>
        </button>`
    }).join("")

    const panel = this.mount(html`
      <div data-actions class="pointer-events-auto absolute top-32 right-8 bottom-8 flex w-[640px] flex-col rounded-3xl bg-navy/90 p-6 shadow-2xl ring-1 ring-white/10 backdrop-blur">
        <div class="flex items-baseline justify-between">
          <h2 class="text-2xl font-bold">Maßnahmen ${this.year()}</h2>
          <p class="text-sm text-white/60">noch <strong class="text-lime">${this.game.budget} Punkte</strong> · ● = 1 Punkt</p>
        </div>
        <p class="mt-1 text-sm text-white/60">Klicken Sie Maßnahmen an, um sie umzusetzen. Die Tonnen zeigen, wie viel CO₂e jede pro Jahr einspart.</p>
        <p class="mt-3 rounded-xl bg-warn/10 px-3 py-2 text-sm text-white/85 ring-1 ring-warn/30">
          <span class="font-semibold text-warn">Ereignis am Jahresende:</span> ${event.title} – 🛡 vorsorgen mit ${eventRemedies(DATA, this.game, event).join(" oder ")}
        </p>
        <div class="mt-3 -mx-1 grid flex-1 auto-rows-max content-start grid-cols-3 gap-2.5 overflow-y-auto p-1">${cards}</div>
        <button data-action="end_round" class="btn btn-lime mt-4 w-full py-4 text-lg">Jahr ${this.year()} abschließen →</button>
      </div>`, false)

    panel.querySelectorAll(".action-card").forEach((card) => {
      card.addEventListener("mouseenter", () => this.world.highlight([card.dataset.station]))
      card.addEventListener("mouseleave", () => this.world.highlight([]))
    })
  }

  async on_buy(button) {
    if (this.busy) return
    const id = button.dataset.id
    const action = DATA.actions.actions[id]
    if (!canBuy(DATA, this.game, id)) return
    this.busy = true
    this.game = buyAction(DATA, this.game, id)
    this.renderStatus()
    this.renderActions()
    this.ui.querySelector("[data-actions]").classList.add("pointer-events-none", "opacity-60")
    this.toast(`<strong>${action.label}</strong> – ${action.text}`)

    if (action.station !== "factory" || id === "carbon_accounting") {
      await this.rig.flyToPoint(this.world.stationPosition(action.station), { distance: id === "carbon_accounting" ? 150 : 60, duration: 1.3 })
    } else {
      await this.rig.flyToPoint(this.world.stationPosition("factory"), { distance: 62, duration: 1.3 })
    }
    await playAnimation(this.world, action.animation)
    if (id === "carbon_accounting") this.applyQuality()
    this.applyEmissions()
    await new Promise((resolve) => setTimeout(resolve, 1700))
    await this.rig.fly(VIEWS.world, { duration: 1.4 })
    this.ui.querySelector("[data-actions]")?.classList.remove("pointer-events-none", "opacity-60")
    this.busy = false
  }

  applyQuality() {
    const quality = stationQuality(DATA, this.game)
    for (const id of this.world.stationIds()) this.world.setQuality(id, quality[id], uncertainty(quality[id]))
    this.world.showQuality(true)
  }

  applyEmissions() {
    const current = emissions(DATA, this.game)
    for (const [id, tonnes] of Object.entries(current.stations)) this.world.setStationTonnes(id, tonnes)
    for (const [id, tonnes] of Object.entries(current.stations)) {
      const base = this.base.stations[id]
      if (base > 0) this.world.stations[id]?.cloud.setClean(Math.min(1, (1 - tonnes / base) * 2))
    }
    this.updateCounter()
  }

  toast(markup) {
    this.ui.querySelector("[data-toast]")?.remove()
    const toast = this.mount(html`
      <div data-toast class="absolute bottom-8 left-8 max-w-xl rounded-2xl bg-white px-6 py-4 text-navy shadow-2xl">
        <p class="text-[15px] leading-snug">${markup}</p>
      </div>`, { from: "translate-y-4" })
    clearTimeout(this.toastTimer)
    this.toastTimer = setTimeout(() => toast.remove(), 7000)
  }

  on_end_round() {
    if (this.busy) return
    const before = this.game
    this.game = endRound(DATA, this.game)
    const outcome = this.game.eventResults.at(-1)
    const event = DATA.events.events[outcome.id]
    this.ui.querySelector("[data-actions]")?.remove()
    this.ui.querySelector("[data-toast]")?.remove()
    if (outcome.id === "supplier_epd" && outcome.success) this.applyQuality()
    const budgetLine = outcome.budget === 0 ? "" : html`
      <p class="mt-3 text-2xl font-bold ${outcome.budget > 0 ? "text-lime" : "text-bad"}">${outcome.budget > 0 ? "+" : "−"}${Math.abs(outcome.budget)} Budgetpunkte</p>`
    this.modal(html`
      <p class="text-sm font-semibold tracking-[0.2em] text-white/60 uppercase">${this.year(before.round)} · Ereignis</p>
      <p class="mt-3 text-6xl">${outcome.success ? "✅" : "⚠️"}</p>
      <p class="mt-3 text-3xl font-bold">${event.title}</p>
      <p class="mt-3 text-lg text-white/80">${outcome.text}</p>
      ${budgetLine}
      <button data-action="${this.game.finished ? "show_result" : "next_round"}" class="btn btn-lime mt-8 px-10 py-4 text-lg">
        ${this.game.finished ? "Zum Ergebnis →" : `Weiter nach ${this.year(this.game.round)} →`}
      </button>`)
  }

  on_next_round(button) {
    button.closest("[data-ui]").remove()
    this.renderStatus()
    this.startRound()
  }

  // --- Phase 6: Ergebnis -----------------------------------------------------

  on_show_result(button) {
    button.closest("[data-ui]").remove()
    this.phase = "result"
    this.exploreEnabled = false
    this.rig.interactive = false
    this.lastInput = Date.now()
    this.result = result(DATA, this.game)
    this.ui.querySelector("[data-status]")?.remove()
    this.world.showLabels(this.world.stationIds(), false)
    this.rig.fly(VIEWS.attract, { duration: 2 }).then(() => (this.rig.autoRotate = 0.05))
    this.stage.setShift(0)
    const r = this.result
    const bar = (label, value, max) => html`
      <div>
        <div class="flex justify-between text-sm"><span class="text-white/70">${label}</span><span class="font-semibold">${value} / ${max}</span></div>
        <div class="mt-1 h-2 rounded-full bg-white/10"><div class="h-2 rounded-full bg-lime" style="width:${(value / max) * 100}%"></div></div>
      </div>`
    this.resultPanel = this.mount(html`
      <div class="pointer-events-auto absolute top-1/2 left-1/2 flex w-[1040px] -translate-x-1/2 -translate-y-1/2 gap-8 rounded-3xl bg-navy/95 p-10 shadow-2xl ring-1 ring-white/10 backdrop-blur">
        <div class="flex-1">
          <p class="text-xs font-semibold tracking-[0.18em] text-lime uppercase">Ihr Ergebnis · ${DATA.industries.industries[this.game.industry].label}</p>
          <div class="mt-2 flex items-end gap-4">
            <p data-score class="text-8xl leading-none font-bold">0</p>
            <p class="mb-2 text-2xl text-white/60">/ 100 Punkte</p>
          </div>
          <div class="mt-6 grid grid-cols-3 gap-3 text-center">
            <div class="rounded-2xl bg-white/5 p-3"><p class="text-3xl font-bold text-lime">−${formatPercent(r.reduction, 1)}</p><p class="text-xs text-white/60">bis ${DATA.actions.rounds.at(-1)}</p></div>
            <div class="rounded-2xl bg-white/5 p-3"><p class="text-3xl font-bold">−${formatPercent(r.scope12Reduction)}</p><p class="text-xs ${r.sbti.scope12OnTrack ? "text-lime" : "text-white/60"}">Scope 1 + 2 ${r.sbti.scope12OnTrack ? "✓ 1,5-°C-Kurs" : `(Ziel −${formatPercent(r.sbti.scope12Target)})`}</p></div>
            <div class="rounded-2xl bg-white/5 p-3"><p class="text-3xl font-bold">−${formatPercent(r.scope3Reduction)}</p><p class="text-xs ${r.sbti.scope3OnTrack ? "text-lime" : "text-white/60"}">Scope 3 ${r.sbti.scope3OnTrack ? "✓ auf Kurs" : `(Ziel −${formatPercent(r.sbti.scope3Target, 1)})`}</p></div>
          </div>
          <div class="mt-6 space-y-3">
            ${bar("Reduktion (bestmöglich: −" + formatPercent(r.best.reduction) + ")", r.points.reduction, 60)}
            ${bar("Budget-Effizienz", r.points.efficiency, 20)}
            ${bar("Schätzung Scope-3-Anteil", r.points.guess, 10)}
            ${bar("Ereignisse gemeistert", r.points.events, 10)}
          </div>
        </div>
        <div data-finish class="flex w-[360px] flex-col justify-center rounded-2xl bg-white/5 p-6">
          ${this.booth ? "" : html`
            <p class="text-xl font-semibold">Ihr Ergebnis speichern</p>
            <p class="mt-1 text-sm text-white/60">Details, VS-Schnellcheck und Kontakt zu be2morrow. Die Bestenliste gilt nur für Spiele an unserem Messestand.</p>
            <button data-action="finish" data-skip class="btn btn-lime mt-5 w-full py-3 text-lg">Ergebnis anzeigen</button>`}
          <div class="${this.booth ? "" : "hidden"}">
          <p class="text-xl font-semibold">Ab auf die Bestenliste!</p>
          <p class="mt-1 text-sm text-white/60">Name oder Spitzname, max. 20 Zeichen. Optional.</p>
          <input data-nickname type="text" maxlength="20" autocomplete="off" spellcheck="false" placeholder="z. B. Klimaheld_42"
                 class="mt-4 w-full rounded-xl border-0 bg-white px-4 py-3 text-lg text-navy placeholder:text-slate focus:ring-2 focus:ring-lime">
          <button data-action="finish" class="btn btn-lime mt-4 w-full py-3 text-lg">Eintragen & QR-Code</button>
          <button data-action="finish" data-skip class="mt-3 text-sm text-white/60 underline">Ohne Eintrag weiter</button>
          </div>
        </div>
      </div>`, { from: "scale-95" })
    countUp(this.resultPanel.querySelector("[data-score]"), 0, r.score, { duration: 1800 })
    if (this.booth) setTimeout(() => this.resultPanel.querySelector("[data-nickname]")?.focus(), 600)
    this.resultPanel.querySelector("[data-nickname]").addEventListener("keydown", (event) => {
      if (event.key === "Enter") this.on_finish(this.resultPanel.querySelector("[data-action=finish]"))
    })
  }

  async on_finish(button) {
    if (this.finishing) return
    this.finishing = true
    const input = this.resultPanel.querySelector("[data-nickname]")
    const nickname = button.hasAttribute("data-skip") ? "" : input.value.trim().replace(/[^\p{L}\p{N} ._-]/gu, "").slice(0, 20)
    const completedAt = Date.now()
    const payload = runPayload(DATA, this.game, { token: this.token, nickname, completedAt })
    const box = this.resultPanel.querySelector("[data-finish]")
    box.innerHTML = '<p class="text-center text-white/60">Speichere Ergebnis …</p>'

    await Promise.race([this.outbox.push(payload), new Promise((resolve) => setTimeout(resolve, 2500))])
    let url = `${this.resultBaseUrl}${this.token}`
    if (!this.outbox.isSynced(this.token)) {
      url += `#d=${encodeRun({
        token: this.token, industry: this.game.industry, employees: this.game.employees, seed: this.game.seed,
        guess: this.game.guess, nickname, completedAt, actions: this.result.actions
      })}`
    }
    const qr = await QRCode.toDataURL(url, { margin: 1, width: 560, errorCorrectionLevel: "M", color: { dark: "#13192e", light: "#ffffff" } })
    const top = this.todayBest === undefined || this.result.score > this.todayBest
    box.innerHTML = html`
      <img src="${qr}" alt="QR-Code zum Ergebnis" class="mx-auto w-56 rounded-xl bg-white p-2">
      <p class="mt-4 text-center text-lg font-semibold">Ergebnis aufs Smartphone</p>
      <p class="mt-1 text-center text-sm text-white/60">${this.booth ? "Details, VS-Schnellcheck, Kontakt & Tagesverlosung" : "Details, VS-Schnellcheck & Kontakt"}</p>
      ${this.booth ? "" : `<a href="${url}" target="_blank" rel="noopener" class="btn btn-lime mt-4 w-full">Ergebnis öffnen</a>`}
      ${nickname && top ? '<p class="mt-3 text-center font-semibold text-lime">🏆 Neuer Tagesbestwert!</p>' : ""}
      <button data-action="restart" class="btn btn-ghost mt-5 w-full">Neues Spiel</button>`
    if (nickname && top) this.confetti()
    this.finishing = false
  }

  on_restart() {
    this.attract()
  }

  confetti() {
    const colors = ["#c1e130", "#47bb63", "#ffffff", "#9aa3b8"]
    for (let i = 0; i < 80; i++) {
      const piece = document.createElement("div")
      piece.className = "pointer-events-none absolute top-0 h-3 w-2 rounded-sm"
      piece.style.left = `${Math.random() * 100}%`
      piece.style.background = colors[i % colors.length]
      this.ui.appendChild(piece)
      gsap.fromTo(piece, { y: -20, rotation: 0 }, {
        y: window.innerHeight + 40, x: (Math.random() - 0.5) * 300, rotation: Math.random() * 720,
        duration: 2.5 + Math.random() * 2, delay: Math.random() * 0.6, ease: "power1.in", onComplete: () => piece.remove()
      })
    }
  }

  // --- Debug -------------------------------------------------------------------

  enableStats() {
    const box = document.createElement("div")
    box.className = "absolute bottom-2 left-2 z-50 rounded bg-black/70 px-2 py-1 font-mono text-xs"
    this.root.appendChild(box)
    let frames = 0
    let last = performance.now()
    this.stage.onUpdate(() => {
      frames++
      const now = performance.now()
      if (now - last > 1000) {
        const info = this.stage.renderer.info.render
        box.textContent = `${frames} fps · ${info.calls} draws · ${info.triangles} tris`
        frames = 0
        last = now
      }
    })
    window.kiosk = this
    window.kioskPlay = (name) => playAnimation(this.world, name)
  }
}
